// src/App.js
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ToastProvider } from "./components/common/Toast";
import Navbar from "./components/layout/Navbar";
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import SportsPage from "./pages/SportsPage";
import ProfilePage from "./pages/ProfilePage";
import AboutPage from "./pages/AboutPage";
import UsersPage from "./pages/admin/UsersPage";
import PermissionsPage from "./pages/admin/PermissionsPage";
import GroupsPage from "./pages/admin/GroupsPage";

// Route guard — redirects to login if not authenticated
function ProtectedRoute() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-tiger-500/30 border-t-tiger-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return (
    <>
      <Navbar />
      <main className="min-h-[calc(100vh-65px)]">
        <Outlet />
      </main>
      <footer className="border-t border-onyx-800/30 py-6 text-center">
        <p className="text-xs font-display text-onyx-700 tracking-widest">
          EVANDER CAMPUS PSAL · BRONX, NY
        </p>
      </footer>
    </>
  );
}

// Admin route guard — requires manage_users permission
function AdminRoute() {
  const { hasPermission, loading } = useAuth();

  if (loading) return null;
  if (!hasPermission("manage_users")) return <Navigate to="/" replace />;

  return <Outlet />;
}

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected */}
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/sports/:sport" element={<SportsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/profile" element={<ProfilePage />} />

              {/* Admin */}
              <Route element={<AdminRoute />}>
                <Route path="/admin/users" element={<UsersPage />} />
                <Route path="/admin/permissions" element={<PermissionsPage />} />
                <Route path="/admin/groups" element={<GroupsPage />} />
              </Route>
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
